import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Radio, Users, Play, Calendar, PlusCircle, Store, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * Marketplace Live shopping index — TikTok-Live-style feed of streams. Both
 * customers and businesses can host. Each session card links to the watch
 * page; hosts see "Go live" / "End stream" controls contextually.
 */
export default function MarketplaceLive() {
  const { user } = useAuth();
  const [tab, setTab] = useState("live");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({ configured: false, provider: "mock" });

  useEffect(() => {
    api.get("/marketplace/live/config").then(r => setConfig(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const status = tab === "all" ? "" : tab;
        const qs = status ? `?status=${status}` : "";
        const r = await api.get(`/marketplace/live/sessions${qs}`);
        if (!alive) return;
        setItems(r.data?.items || []);
        setTotal(r.data?.total || 0);
      } catch (e) {
        if (alive) toast.error(formatApiError(e.response?.data?.detail));
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [tab]);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-10" data-testid="marketplace-live-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-[#B31E1E]">Marketplace · Live</div>
          <h1 className="font-display text-4xl tracking-tighter mt-1">Live shopping</h1>
          <p className="mt-2 text-gray-600 max-w-2xl">
            Watch sellers demo their items in real time and buy without leaving the stream.
            Anyone with a Servrevu account — customer or business — can go live.
          </p>
        </div>
        {user ? (
          <Link to="/marketplace/live/new">
            <Button className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid="live-new-button">
              <PlusCircle size={14} className="mr-2" /> Go live
            </Button>
          </Link>
        ) : (
          <Link to="/login"><Button className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white">Sign in to go live</Button></Link>
        )}
      </header>

      {!config.configured && (
        <Card className="rounded-md p-4 mt-5 bg-amber-50 border-amber-200 text-xs text-amber-900" data-testid="mock-banner">
          <strong>Preview mode:</strong> live video ingest is running in <em>mock</em> — no real streaming is happening. Set <code className="font-mono">MUX_TOKEN_ID</code> + <code className="font-mono">MUX_TOKEN_SECRET</code> in backend env to enable actual live playback.
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="rounded-sm">
          <TabsTrigger value="live" data-testid="tab-live">Live now</TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="tab-scheduled">Upcoming</TabsTrigger>
          <TabsTrigger value="ended" data-testid="tab-ended">Recent</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <p className="text-sm text-gray-500 mt-6">Loading…</p>
      ) : items.length === 0 ? (
        <Card className="rounded-md p-10 mt-6 text-center text-gray-600" data-testid="live-empty">
          <Radio className="mx-auto mb-2 text-gray-400" size={28} />
          Nobody's live right now — {user ? <Link to="/marketplace/live/new" className="text-[#B31E1E] hover:underline">go first</Link> : "sign in to broadcast"}.
        </Card>
      ) : (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="live-grid">
          {items.map(s => <LiveCard key={s.id} s={s} />)}
        </div>
      )}
    </div>
  );
}

function LiveCard({ s }) {
  return (
    <Link to={`/marketplace/live/${s.id}`} data-testid={`live-card-${s.id}`}>
      <Card className="rounded-md overflow-hidden hover:border-[#B31E1E]/40 transition-colors">
        <div className="aspect-video bg-gray-900 relative">
          {s.cover_image ? (
            <img src={s.cover_image} alt={s.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500">
              <Radio size={32} />
            </div>
          )}
          {s.status === "live" && (
            <Badge className="absolute top-2 left-2 rounded-sm bg-red-600 animate-pulse">
              <span className="w-2 h-2 rounded-full bg-white mr-1.5 inline-block" /> LIVE
            </Badge>
          )}
          {s.status === "scheduled" && s.scheduled_for && (
            <Badge className="absolute top-2 left-2 rounded-sm bg-amber-500">
              <Calendar size={9} className="mr-1" /> {new Date(s.scheduled_for).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </Badge>
          )}
          {s.status === "ended" && (
            <Badge className="absolute top-2 left-2 rounded-sm bg-gray-500">Replay</Badge>
          )}
          {s.host_type === "business" && (
            <Badge className="absolute top-2 right-2 rounded-sm bg-[#B31E1E] text-white text-[10px]"><Store size={9} className="mr-1" /> Business</Badge>
          )}
          <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
            <Users size={11} /> {s.viewer_count || 0}
          </div>
        </div>
        <div className="p-3">
          <div className="font-medium truncate">{s.title}</div>
          <div className="text-[11px] text-gray-500 truncate mt-0.5">
            {s.host_type === "business" ? (s.host_business_name || s.host_name) : s.host_name}
            {s.listing_ids?.length > 0 ? ` · ${s.listing_ids.length} item${s.listing_ids.length === 1 ? "" : "s"}` : ""}
          </div>
        </div>
      </Card>
    </Link>
  );
}
